import { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import { io, Socket } from "socket.io-client";

import { API_HOSTNAME } from "@/constants";
import { auth } from "@/utils/firebase";

interface UseSocketIOOptions {
    lessonId: string | null;
    enabled?: boolean;
}

interface UseSocketIOReturn {
    socket: Socket | null;
    isConnected: boolean;
    error: Error | null;
}

export function useSocketIO({ lessonId, enabled = true }: UseSocketIOOptions): UseSocketIOReturn {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        if (!enabled || !lessonId) {
            return;
        }

        const initializeSocket = async () => {
            try {
                const user = auth.currentUser;
                if (!user) {
                    setError(new Error("User not authenticated"));
                    return;
                }

                const newSocket = io(API_HOSTNAME, {
                    auth: (cb) => {
                        auth.currentUser?.getIdToken(true)
                            .then((token) => cb({ token }))
                            .catch(() => cb({ token: null }));
                    },
                    transports: ["websocket", "polling"],
                    reconnection: true,
                    reconnectionDelay: 1000,
                    reconnectionAttempts: 5,
                });

                socketRef.current = newSocket;
                setSocket(newSocket);

                // Connection event handlers
                newSocket.on("connect", () => {
                    setIsConnected(true);
                    setError(null);

                    Sentry.addBreadcrumb({
                        category: "whiteboard",
                        message: "Socket connected, joining whiteboard room",
                        level: "info",
                        data: {
                            lessonId,
                            socketId: newSocket.id
                        },
                    });

                    // Join the whiteboard room for this lesson (auth callback sends fresh token on each handshake)
                    newSocket.emit("whiteboard:join", { lessonId });
                });

                newSocket.on("disconnect", (reason) => {
                    setIsConnected(false);

                    Sentry.addBreadcrumb({
                        category: "whiteboard",
                        message: "Socket disconnected",
                        level: "info",
                        data: {
                            lessonId,
                            reason
                        },
                    });
                });

                newSocket.on("connect_error", (err) => {
                    console.error("[Socket.IO] Connection error:", err);
                    setError(err);
                    setIsConnected(false);
                    Sentry.captureException(err, {
                        tags: {
                            area: "whiteboard",
                            op: "connect_error"
                        },
                        extra: { lessonId },
                    });
                });

                newSocket.on("error", (err: { message?: string }) => {
                    const message = err?.message || "Socket.IO error";
                    console.error("[Socket.IO] Error:", err);
                    setError(new Error(message));

                    // Capture server-sent errors (e.g. "Not joined to any lesson room") for debugging
                    if (message.includes("Not joined") || message.includes("lesson room")) {
                        Sentry.captureMessage("Whiteboard: server rejected event - not in lesson room", {
                            level: "warning",
                            tags: {
                                area: "whiteboard",
                                op: "server_error"
                            },
                            extra: {
                                lessonId,
                                socketId: newSocket.id,
                                serverMessage: message
                            },
                        });
                    }
                });
            } catch (err) {
                console.error("[Socket.IO] Initialization error:", err);
                setError(err instanceof Error ? err : new Error("Failed to initialize Socket.IO"));
            }
        };

        initializeSocket();

        // Cleanup on unmount or when lessonId/enabled changes
        return () => {
            if (socketRef.current) {
                socketRef.current.emit("whiteboard:leave");
                socketRef.current.disconnect();
                socketRef.current = null;
                setSocket(null);
                setIsConnected(false);
            }
        };
    }, [lessonId, enabled]);

    return {
        socket,
        isConnected,
        error
    };
}
